<template>
  <div v-if="enItinerary.id" class="bg-beige1">
    <MainVisualSectionCMS
      v-if="enItinerary.titleEn && backgroundWideMediaUrls.length"
      button-label="Request itinerary arrangements"
      title-size-preset="itinerary"
      :images="imgUrls"
      :is-sp="isSP"
      :on-button-click="openContactDrawer"
      :pc-images="backgroundWideMediaUrls"
      :sp-images="spImageUrls"
      :summary="enItinerary.summary"
      :title-en="enItinerary.titleEn"
      :title-tag="itineraryTagLabel"
    />
    <TitleSection v-else :title="enItinerary.titleEn" />
    <template v-if="enItinerary.contents?.length">
      <div ref="contentsContainerRef">
        <template v-for="(content, contentIndex) in enItinerary.contents" :key="`${content.fieldId}-${contentIndex}`">
          <!-- Story セクション -->
          <div v-if="content?.fieldId === 'storyContents'" class="flex w-full flex-col items-start bg-black py-8 md:items-center">
            <div v-for="storyContent in content.storyContents" :key="storyContent.fieldId" class="bg-black">
              <h2 v-if="storyContent.fieldId === 'storyContentTitle'" class="px-4 text-center text-2xl text-gold4 md:text-3xl">{{ storyContent.title }}</h2>
              <div v-if="storyContent.fieldId === 'stories'">
                <StorySliderSection :stories="storyContent.stories" />
              </div>
            </div>
          </div>

          <!-- Article セクション -->
          <ArticleSection v-if="content?.fieldId === 'articleSection'" :experiences="experiences" :on-cta-link-click="handleCtaLinkClick" :section="content" />

          <!-- Experience Section IDs セクション -->
          <div v-if="content?.fieldId === 'experienceSectionIDs' && experienceSections.length > 0" class="max-w-[1208px] py-8 md:mx-auto md:w-[90%] md:px-8" data-testid="experience-section">
            <template v-for="microCMSExperienceSection in content.experienceSectionIDs" :key="microCMSExperienceSection.experienceSectionId">
              <ExperienceSection
                :experience-sections="experienceSectionsByWebId.get(microCMSExperienceSection.experienceSectionId) ?? []"
                :on-experience-click="handleExperienceClick"
                :on-view-all="handleViewAll"
                :should-show-gift-card="false"
              />
            </template>
          </div>
        </template>
      </div>
      <div ref="itineraryRequestRef">
        <ItineraryContactSection
          :background-wide-media-url="backgroundWideMediaUrls[0]"
          :itinerary-tag-label="itineraryTagLabel"
          :itinerary-title="enItinerary.titleEn"
          :micro-c-m-s-content-id="contentId"
          :page-url="itineraryPageUrl"
          :price-text="itineraryPriceText"
        />
      </div>
      <ClientOnly>
        <RichTextContentDrawer :content="activeDrawerContent" :on-open-change="handleDrawerOpenChange" :open="isDrawerOpen" />
      </ClientOnly>
    </template>
    <ContactLinkSection :on-click-contact="onClickContact" :on-click-partner="onClickPartner" />
    <ClientOnly>
      <ItineraryContactCTABar v-if="enItinerary.contents?.length" :itinerary-tag-label="itineraryTagLabel" :on-click-open-request="openContactDrawer" :title="enItinerary.titleEn" />
    </ClientOnly>
    <!-- 問い合わせドロワー（CTA・MainVisualボタンで右から表示） -->
    <ItineraryContactDrawer
      :background-wide-media-url="backgroundWideMediaUrls[0] ?? ''"
      :itinerary-tag-label="itineraryTagLabel"
      :itinerary-title="enItinerary.titleEn"
      :micro-c-m-s-content-id="contentId"
      :on-close="closeContactDrawer"
      :on-submit-success="handleItinerarySubmitSuccess"
      :open="isContactDrawerOpen"
      :page-url="itineraryPageUrl"
      :price-text="itineraryPriceText"
    />
    <!-- 問い合わせ送信成功時（ドロワーを閉じた後に表示） -->
    <MoleculesModal :on-close="closeThankYouModal" :should-show-modal="isThankYouModalOpen">
      <ItineraryThankYouMessage />
    </MoleculesModal>
  </div>
</template>

<script setup lang="ts">
import { ContactLinkSection, useBuildUrl, useErrorCreator, useIsSP, useMicroCMSParams } from '@/wabunka-src/common';
import { useLastModifiedHeader } from '@/wabunka-src/common/composables/general/useLastModifiedHeader/useLastModifiedHeader';
import { ExperienceSection, StorySliderSection, useExtractExperienceIds, useExtractWebSectionIds, buildMetaData } from '@/wabunka-src/common/features';
import ArticleSection from '@/wabunka-src/common/features/Featured/components/ArticleSection/ArticleSection.vue';
import RichTextContentDrawer from '@/wabunka-src/common/features/Featured/components/RichTextContentDrawer/RichTextContentDrawer.vue';
import { useRichTextDrawer } from '@/wabunka-src/common/features/Featured/composables/useRichTextDrawer';
import { extractDrawerContents } from '@/wabunka-src/common/features/Featured/models/featured/featured';
import MainVisualSectionCMS from '@/wabunka-src/common/features/MainVisual/components/MainVisualSectionCMS/MainVisualSectionCMS.vue';
import { extractImageUrls } from '@/wabunka-src/common/utils';
import { sendGAWabunkaClickShowRequestItineraryArrangementsEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickShowRequestItineraryArrangements';
import { sendGAWabunkaSectionSlugViewEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionSlugView';
import { sendGAWabunkaSectionViewAllEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/sectionViewAll';
import { EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS } from '@/wabunka-src/constants/experienceSectionWebPagePath';
import { ICON_FONT_URL } from '@/wabunka-src/constants/iconFontUrl';
import { useGetExperienceSectionsQuery, useFetchExperiencesQuery } from '@/wabunka-src/features/Experience';
import { useGetItineraryQuery } from '@/wabunka-src/features/Itinerary/composables/query/useGetItineraryQuery';
import { isItineraryRequestLink } from '@/wabunka-src/features/Itinerary/constants/itineraryContact';
import { DEFAULT_ITINERARY_TAG_LABEL, getItineraryTagLabel } from '@/wabunka-src/features/Itinerary/constants/itineraryTagLabels';
import ItineraryContactCTABar from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/ItineraryContactCTABar/ItineraryContactCTABar.vue';
import ItineraryContactDrawer from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/ItineraryContactDrawer/ItineraryContactDrawer.vue';
import ItineraryThankYouMessage from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/ItineraryThankYouMessage/ItineraryThankYouMessage.vue';
import TitleSection from '@/wabunka-src/features/Itinerary/pages/Itinerary/components/TitleSection/TitleSection.vue';

const route = useRoute();
const { createWabunkaError } = useErrorCreator();

const { contentId, draftKeyQuery, hasDraftKey } = useMicroCMSParams(route);
const { enItinerary, isNotFound, hasError } = await useGetItineraryQuery(contentId, draftKeyQuery, true, hasDraftKey);

if (isNotFound.value) {
  // 取得失敗だけ Sentry に送る。itinerary 以外を開いたケースは仕様どおりの 404 なので送らない
  throw hasError.value ? createWabunkaError({ statusCode: 404, fatal: true }) : createError({ statusCode: 404, fatal: true });
}
useLastModifiedHeader(enItinerary.value.revisedAt);

const { webSectionIds } = useExtractWebSectionIds(enItinerary);
const { experienceSections } = await useGetExperienceSectionsQuery(EXPERIENCE_SECTION_DISPLAY_WEB_PAGE_PATHS.ITINERARY_DETAIL, webSectionIds.value);
const { experienceIds } = useExtractExperienceIds(enItinerary);
const experiencesData =
  experienceIds.value?.length > 0
    ? await useFetchExperiencesQuery({
        experienceEventStartAtDates: ref(''),
        destinations: ref(''),
        themes: ref(''),
        slugs: experienceIds,
      })
    : { experiences: computed(() => []) };
const experiences = experiencesData.experiences;

const { buildUrl, buildUrlWithEndSlash } = useBuildUrl();
const itineraryPageUrl = computed(() => buildUrl(route.path));
const isSP = useIsSP();
const backgroundWideMediaUrls = computed(() => extractImageUrls(enItinerary.value.backgroundWideMedias));
const spImageUrls = computed(() => extractImageUrls(enItinerary.value.backgroundTallMedias));
const imgUrls = computed(() => (isSP.value ? spImageUrls.value : backgroundWideMediaUrls.value));

/** 問い合わせドロワー開閉状態 */
const isContactDrawerOpen = ref(false);

/** Thank you モーダル表示状態（送信成功後にドロワーを閉じてから表示） */
const isThankYouModalOpen = ref(false);

/** 日数に応じたタグラベル（ContactSection用。未設定時はデフォルト文言） */
const itineraryTagLabel = computed(() => getItineraryTagLabel(enItinerary.value.itineraryTagSettings?.days) ?? DEFAULT_ITINERARY_TAG_LABEL);

/** 価格帯テキスト（API の itineraryTagSettings.price、未設定なら表示しない） */
const itineraryPriceText = computed(() => {
  const price = enItinerary.value.itineraryTagSettings?.price;
  return price !== undefined && price !== '' ? price : undefined;
});

const contentsContainerRef = ref<HTMLElement | null>(null);
const drawerContents = computed(() => extractDrawerContents(enItinerary.value.contents ?? []));

/** webSectionId をキーにした experienceSections のマップ */
const experienceSectionsByWebId = computed(() => {
  const map = new Map<string, (typeof experienceSections.value)[number][]>();
  for (const section of experienceSections.value) {
    const id = section.webSectionId;
    const list = map.get(id) ?? [];
    list.push(section);
    map.set(id, list);
  }
  return map;
});
const { isOpen: isDrawerOpen, activeContent: activeDrawerContent } = useRichTextDrawer(contentsContainerRef, drawerContents);

const handleDrawerOpenChange = (open: boolean) => {
  isDrawerOpen.value = open;
};

/** KV・固定CTAバーのボタン押下時：問い合わせドロワーを開く */
const openContactDrawer = () => {
  sendGAWabunkaClickShowRequestItineraryArrangementsEvent(route.path);
  isContactDrawerOpen.value = true;
};

/**
 * Article セクション内の CTA クリック時に呼ばれる。
 * リンクが #itinerary-request の場合は遷移せず問い合わせドロワーを開き true を返す。
 */
const handleCtaLinkClick = (link: string): boolean => {
  if (!isItineraryRequestLink(link)) {
    return false;
  }
  openContactDrawer();
  return true;
};

/** 問い合わせドロワーを閉じる（子から onClose で呼ばれる） */
const closeContactDrawer = () => {
  isContactDrawerOpen.value = false;
};

/** 問い合わせ送信成功時：ドロワーを閉じてから Thank you モーダルを表示 */
const handleItinerarySubmitSuccess = () => {
  isContactDrawerOpen.value = false;
  isThankYouModalOpen.value = true;
};

/** Thank you モーダルを閉じる */
const closeThankYouModal = () => {
  isThankYouModalOpen.value = false;
};

/** コンタクトボタンを押した際のイベントハンドラ */
const onClickContact = () => {
  navigateTo('/contact/');
};

/** パートナーボタンを押した際のイベントハンドラ */
const onClickPartner = () => {
  navigateTo('/lp/partner-with-us/');
};

/** 体験カードがクリックされた際のイベントハンドラ */
const handleExperienceClick = (sectionId: string, experienceSlug: string) => {
  sendGAWabunkaSectionSlugViewEvent(sectionId, experienceSlug, route.name as string);
};

/** もっと見るボタンがクリックされた際のイベントハンドラ */
const handleViewAll = (sectionId: string) => {
  sendGAWabunkaSectionViewAllEvent(sectionId, route.name as string);
};

useServerSeoMeta({
  ...buildMetaData(enItinerary.value, { ogUrl: buildUrlWithEndSlash(route.path), defaultOgImage: buildUrl('/en_ogp.png') }),
});

// プレビューは itinerary 以外も表示されうるため index させない
if (hasDraftKey) useRobotsRule('noindex,nofollow');

useHead({
  link: [
    // プレビューは noindex なので canonical を出さない（noindex が正規 URL 側へ転移するのを避ける）
    ...(hasDraftKey ? [] : [{ rel: 'canonical' as const, href: buildUrlWithEndSlash(route.path) }]),
    { rel: 'stylesheet', href: ICON_FONT_URL },
  ],
});
</script>
